import React from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { get } from 'lodash-es';
import TextField from '@material-ui/core/TextField';
import Grid from '@material-ui/core/Grid';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

import Replace from '../../../../components/Replace/Replace';
import tick from './tick.png';

import { withStyles } from '@material-ui/core/styles';
import styles from './styles';
import { Icon } from '@material-ui/core';

import { iPhoneFriendlyButtonLabel } from '../../../../helpers/stringHelpers';
import { getString } from '../../../../helpers/stringHelpers';

class DecisionTool extends React.Component {
	state = {
		visibility: {},
		progress: {},
		result: null,
		checked: {},
		submissionComplete: false,
		comments: '',
	};

	constructor(props) {
		super(props);

		const { questions } = this.props;

		questions.forEach((question, index) => {
			if (index === 0) {
				this.state.visibility[question.key] = true;
			} else {
				this.state.visibility[question.key] = false;
			}
			this.state.progress[question.key] = null;
		});

		this.myRef = React.createRef();

		this.showNextQuestion = this.showNextQuestion.bind(this);
		this.updateProgress = this.updateProgress.bind(this);
		this.updateResult = this.updateResult.bind(this);
		this.buildProgressTracker = this.buildProgressTracker.bind(this);
		this.buildAnswerButtons = this.buildAnswerButtons.bind(this);
		this.buildCheckOptions = this.buildCheckOptions.bind(this);
		this.handleCheckChange = this.handleCheckChange.bind(this);
		this.handleCommentsChange = this.handleCommentsChange.bind(this);
		this.getQuestionResponses = this.getQuestionResponses.bind(this);
		this.getCommentsField = this.getCommentsField.bind(this);
		this.submissionComplete = this.submissionComplete.bind(this);
		this.shouldCommentsFieldShow = this.shouldCommentsFieldShow.bind(this);
		this.shouldSubmitButtonShow = this.shouldSubmitButtonShow.bind(this);
		this.shouldExclamationShow = this.shouldExclamationShow.bind(this);
	}

	showNextQuestion(questionKey) {
		const { questions } = this.props;

		const newVisibility = { ...this.state.visibility };

		// Show the question
		newVisibility[questionKey] = true;

		// Hide mutually exclusive questions
		const question = questions.find(
			(question) => question.key === questionKey
		);

		question.mutexQuestions.forEach((mutexQuestion) => {
			newVisibility[mutexQuestion] = false;
		});

		this.setState({ visibility: newVisibility }, () => {
			let offset = -100;
			if (question.hasOwnProperty('checkOptions')) {
				offset = -200;
			}
			window.scrollTo(0, this.myRef.offsetTop + offset);
		});
	}

	updateProgress(question, answer) {
		const { questions } = this.props;

		const additional = [];
		if (question.hasOwnProperty('checkOptions')) {
			question.checkOptions.forEach((option) => {
				const key = `${question.key} ${option.key}`;
				const checked = get(this.state.checked, key, false);
				if (checked) {
					additional.push(option.title);
				}
			});
		}

		let newProgress = { ...this.state.progress };
		newProgress[question.key] = {
			answer: answer,
			additional: additional,
			questionTitle: question.title,
		};

		// If this question has checkOptions and none of them are checked, remove the progress item
		if (
			question.hasOwnProperty('checkOptions') &&
			additional.length === 0
		) {
			newProgress[question.key] = null;
		}

		let newVisibility = { ...this.state.visibility };

		// Wipe all progress beyond the answered question
		let wipeProgress = false;
		let hideNextQuestion = false;
		questions.forEach((q) => {
			if (hideNextQuestion) {
				newVisibility[q.key] = false;
				if (q.hasOwnProperty('checkOptions')) {
					// This resets all check options (only one checkbox list per decision tool is supported)
					this.setState({ checked: {} });
				}
			}
			if (wipeProgress) {
				newProgress[q.key] = null;
				hideNextQuestion = true;
			}
			if (q.key === question.key) {
				wipeProgress = true;
			}
		});

		this.setState(
			{ progress: newProgress, visibility: newVisibility },
			() => {
				this.updateResult();

				if (answer.nextQuestion) {
					this.showNextQuestion(answer.nextQuestion);
				} else {
					if (!question.hasOwnProperty('checkOptions')) {
						setTimeout(
							() =>
								window.scrollTo(0, this.myRef.offsetTop - 250),
							1
						);
					}
				}
			}
		);
	}

	getFinalCombination() {
		const finalCombination = [];
		Object.entries(this.state.progress).forEach(
			([questionKey, progressItem]) => {
				const answer = get(progressItem, 'answer', null);
				if (answer) {
					finalCombination.push(`${questionKey} ${answer.key}`);
				}
			}
		);

		finalCombination.sort();

		return finalCombination;
	}

	updateResult() {
		const { results } = this.props;

		const finalCombination = this.getFinalCombination();

		// See if the question/answer combination matches a result
		const result = results.find((result) =>
			result.answerCombinations.find((answerCombination) => {
				answerCombination.sort();
				return (
					JSON.stringify(answerCombination) ===
					JSON.stringify(finalCombination)
				);
			})
		);

		this.setState({ result: result });
	}

	buildProgressTracker() {
		const { classes } = this.props;

		const visibleProgressItems = Object.values(this.state.progress).filter(
			(progressItem) => progressItem !== null
		);

		const progressComponents = [];

		if (visibleProgressItems.length > 0) {
			progressComponents.push(
				<Typography component='h2' className={classes.answersHeading}>
					Your Answers:
				</Typography>
			);
			visibleProgressItems.forEach((progressItem) => {
				const additional = progressItem.additional;

				progressComponents.push(
					<Typography component='span' variant='body1'>
						- {progressItem.answer.progressText}
						{additional.length > 0 && (
							<ul className={classes.additional}>
								{additional.map((additionalItem, i) => (
									<li key={`li-${i}`}>{additionalItem}</li>
								))}
							</ul>
						)}
					</Typography>
				);
			});
		}

		if (this.state.submissionComplete && this.state.comments) {
			progressComponents.push(
				<Typography component='span' variant='body1'>
					- Comments: {this.state.comments}
				</Typography>
			);
		}

		return progressComponents;
	}

	buildAnswerButtons(question) {
		const { classes } = this.props;

		const answerButtons = [];

		question.answers.forEach((answer) => {
			if (!get(answer, 'hidden', false)) {
				const buttonClass =
					this.state.progress[question.key] &&
					this.state.progress[question.key].answer.key === answer.key
						? `${classes.buttonActive} ${classes.button}`
						: classes.button;
				answerButtons.push(
					<Button
						className={buttonClass}
						onClick={() => {
							this.updateProgress(question, answer);
						}}
						disabled={this.state.submissionComplete}
					>
						{iPhoneFriendlyButtonLabel(
							answer.title,
							this.state.submissionComplete
						)}
					</Button>
				);
			}
		});

		return answerButtons;
	}

	buildCheckOptions(question) {
		const { classes } = this.props;

		if (question.hasOwnProperty('checkOptions')) {
			const checkOptions = question.checkOptions.map((option, i) => {
				const key = `${question.key} ${option.key}`;
				return (
					<React.Fragment key={`checkbox-${i}`}>
						<FormControlLabel
							control={
								<Checkbox
									name={key}
									checked={this.state.checked[key] || false}
									onChange={(e) => {
										this.handleCheckChange(e, question);
									}}
								/>
							}
							label={option.title}
							className={classes.checkboxLabel}
						/>
						<br />
					</React.Fragment>
				);
			});

			return checkOptions;
		}
		return [];
	}

	handleCheckChange(e, question) {
		// Disable checkbox if submission is complete
		if (this.state.submissionComplete) {
			return;
		}

		const checked = { ...this.state.checked };
		checked[e.target.name] = e.target.checked;

		// Uncheck mutally exclusive options
		if (e.target.checked) {
			const keyParts = e.target.name.split(' ');
			const optionKey = keyParts[1];
			const checkOption = question.checkOptions.find(
				(option) => option.key === optionKey
			);
			checkOption.mutexOptions.forEach((mutexOption) => {
				const mutexOptionKey = `${question.key} ${mutexOption}`;
				checked[mutexOptionKey] = false;
			});
		}

		this.setState({ checked: checked }, () =>
			this.updateProgress(question, question.answers[0])
		);
	}

	handleCommentsChange(e) {
		this.setState({ ...this.state, comments: e.target.value });
	}

	getQuestionResponses() {
		const responses = [];
		Object.values(this.state.progress).forEach((item) => {
			if (item) {
				if (item.additional.length > 0) {
					const additionalText = item.additional.join(', ');
					responses.push({
						question: item.questionTitle,
						answer: additionalText,
					});
				} else {
					responses.push({
						question: item.questionTitle,
						answer: item.answer.title,
					});
				}
			}
		});

		if (this.state.comments) {
			responses.push({
				question: 'Comments',
				answer: this.state.comments,
			});
		}

		return responses;
	}

	shouldCommentsFieldShow() {
		const { additionalCommentsDisplayConditions } = this.props;

		const finalCombination = this.getFinalCombination();

		// See if the question/answer combination matches the condition for showing comments field
		const showCommentsField = additionalCommentsDisplayConditions.some(
			(combination) => {
				combination.sort();
				return (
					JSON.stringify(combination) ===
					JSON.stringify(finalCombination)
				);
			}
		);

		return showCommentsField;
	}

	shouldSubmitButtonShow() {
		const { hideSubmitConditions } = this.props;

		const finalCombination = this.getFinalCombination();

		// See if the question/answer combination matches the condition for showing comments field
		const hideSubmitButton = hideSubmitConditions.some((combination) => {
			combination.sort();
			return (
				JSON.stringify(combination) === JSON.stringify(finalCombination)
			);
		});

		return !hideSubmitButton;
	}

	shouldExclamationShow() {
		const { showExclamationConditions } = this.props;

		const finalCombination = this.getFinalCombination();

		// See if the question/answer combination matches the condition for showing comments field
		const showExclamation = showExclamationConditions.some(
			(combination) => {
				combination.sort();
				return (
					JSON.stringify(combination) ===
					JSON.stringify(finalCombination)
				);
			}
		);

		return showExclamation;
	}

	getCommentsField() {
		const { classes } = this.props;

		const showCommentsField = this.shouldCommentsFieldShow();

		if (showCommentsField) {
			return (
				<div>
					<TextField
						className={classes.textarea}
						name='comments'
						multiline
						rows={8}
						onChange={this.handleCommentsChange}
						label={getString('decisionTool', 'COMMENTS_LABEL')}
						inputProps={{
							maxLength: 2000,
							'aria-label': 'comments',
						}}
						InputLabelProps={{
							classes: {
								root: classes.label,
								shrink: 'shrink',
							},
							htmlFor: 'comments',
						}}
						value={this.state.comments}
					/>
					<br />
					<br />
				</div>
			);
		} else {
			return null;
		}
	}

	submissionComplete() {
		this.setState({ submissionComplete: true });
	}

	render() {
		const { classes, title, questions, handleSubmit, handleClose, icon } =
			this.props;

		const Icon = icon;

		return (
			<Grid container spacing={24} className='DashGridContainer'>
				<Grid
					item
					xs={12}
					sm={12}
					md={12}
					lg={12}
					className={`${classes.gridItem} DashGridColumn`}
				>
					<div className={classes.container}>
						<style
							dangerouslySetInnerHTML={{
								__html: [
									'.legend {',
									'border-top: 1px solid #bdbdbd;',
									'margin-top: 40px;',
									'padding-top: 40px;',
									'position: relative;',
									'}',
									'.legend:before {',
									'content: "";',
									'display: block;',
									'width: 0;',
									'height: 0;',
									'position: absolute;',
									'border-left: 20px solid transparent;',
									'border-right: 20px solid transparent;',
									'border-top: 18px solid #bdbdbd;',
									'top: -1px;',
									'left: 50%;',
									'transform: translate(-50%);',
									'}',
									'.legend:after {',
									'content: "";',
									'display: block;',
									'width: 0;',
									'height: 0;',
									'position: absolute;',
									'border-left: 20px solid transparent;',
									'border-right: 20px solid transparent;',
									'border-top: 18px solid #ffffff;',
									'top: -3px;',
									'left: 50%;',
									'transform: translate(-50%);',
									'}',
								].join('\n'),
							}}
						></style>

						<Typography
							component='div'
							className={classes.mainHeading}
						>
							<div className={classes.iconWrapper}>
								<Icon
									fontSize='large'
									className={classes.icon}
								/>
							</div>
							&nbsp; {title}
						</Typography>

						{questions.map((question, i) => (
							<div
								className={
									this.state.visibility[question.key]
										? classes.show
										: classes.hide
								}
								key={`div=${i}`}
								ref={(ref) => {
									if (this.state.visibility[question.key]) {
										return (this.myRef = ref);
									} else {
										return null;
									}
								}}
							>
								{i === 0 ? (
									<Typography
										variant='body1'
										component='span'
										className={classes.question}
									>
										{question.title}
									</Typography>
								) : (
									<legend className='legend'>
										<Typography
											variant='body1'
											component='span'
											className={classes.question}
										>
											{question.title}
										</Typography>
									</legend>
								)}
								<br />
								{this.buildCheckOptions(question)}
								<div className={classes.answerButtons}>
									{this.buildAnswerButtons(question)}
								</div>
							</div>
						))}
						<br />
						<br />
						{this.buildProgressTracker()}
						<br />
						{this.state.result && !this.state.submissionComplete && (
							<React.Fragment>
								<div
									className={classes.center}
									ref={(ref) => (this.myRef = ref)}
								>
									<Typography
										component='span'
										className={classes.result}
									>
										{
											<Replace
												content={this.state.result.body}
												toReplace='0800 801 601'
												replaceWith={
													<a
														className={classes.link}
														href='tel:0800 801 601'
													>
														0800 801 601
													</a>
												}
											/>
										}
									</Typography>
									{this.shouldExclamationShow() ? (
										<React.Fragment>
											<br />
											<ErrorOutlineIcon
												className={classes.exclamation}
											/>
										</React.Fragment>
									) : null}
								</div>
								<br />
								{this.getCommentsField()}
								<div className={classes.buttons}>
									<Button
										variant='outlined'
										onClick={handleClose}
									>
										Cancel
									</Button>
									{this.shouldSubmitButtonShow() ? (
										<Button
											variant='outlined'
											color='primary'
											onClick={() => {
												const responses =
													this.getQuestionResponses();
												handleSubmit(responses);
												this.submissionComplete();
											}}
											disabled={
												this.shouldCommentsFieldShow() &&
												!this.state.comments
											}
										>
											{iPhoneFriendlyButtonLabel(
												'Submit',
												this.shouldCommentsFieldShow() &&
													!this.state.comments
											)}
										</Button>
									) : null}
								</div>
							</React.Fragment>
						)}
						{this.state.submissionComplete && (
							<React.Fragment>
								<div className={classes.center}>
									<Typography
										component='span'
										className={classes.result}
									>
										Thank you for your submission, we will
										review your answers shortly.
									</Typography>
									<br />
									<br />
									<img src={tick} alt='tick' title='tick' />
									<br />
								</div>
								<div className={classes.buttons}>
									<Button
										variant='outlined'
										onClick={handleClose}
									>
										Close
									</Button>
								</div>
							</React.Fragment>
						)}
					</div>
				</Grid>
			</Grid>
		);
	}
}

DecisionTool.propTypes = {
	title: PropTypes.string.isRequired,
	questions: PropTypes.array.isRequired,
	results: PropTypes.array.isRequired,
};

export default withStyles(styles, { withTheme: true })(DecisionTool);
